import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Chip,
} from '@mui/joy';
import {
  Wrench as ServiceIcon,
  Info as AboutIcon,
  Star as TestimonialsIcon,
  Images as GalleryIcon,
  Tag as OfferIcon,
  Chats as FAQIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface PageTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  slug: string;
  sections: any[];
  seo: {
    title: string;
    description: string;
  };
  category: 'essential' | 'trust' | 'seo' | 'conversion';
}

interface PageTemplatesProps {
  siteId: number;
  existingPages: string[];
  onPageCreated: () => void;
}

const PAGE_TEMPLATES: PageTemplate[] = [
  // Essential Pages
  {
    id: 'about',
    name: 'About Us',
    description: 'Build trust with your company story and experience',
    icon: <AboutIcon />,
    slug: 'about',
    category: 'essential',
    seo: {
      title: 'About Us - Trusted Roofing Contractors',
      description: 'Learn about our experienced team and commitment to quality roofing services.',
    },
    sections: [
      {
        type: 'hero',
        data: {
          title: 'About Our Company',
          subtitle: 'Trusted roofing experts serving the community',
        }
      },
      {
        type: 'features',
        data: {
          title: 'Why Choose Us',
          features: [
            { title: 'Family Owned', description: 'Three generations of roofing excellence' },
            { title: 'Fully Licensed', description: 'Licensed, bonded, and insured for your protection' },
            { title: 'Warranty Backed', description: 'Industry-leading warranties on all work' },
            { title: 'Community Focused', description: 'Proud to serve our local community' },
          ]
        }
      },
      {
        type: 'cta_band',
        data: {
          title: 'Ready to Work with Trusted Professionals?',
          button_text: 'Get Your Free Quote',
          button_url: '/contact',
        }
      }
    ],
  },
  {
    id: 'gallery',
    name: 'Project Gallery',
    description: 'Showcase your best work with before/after photos',
    icon: <GalleryIcon />,
    slug: 'gallery',
    category: 'trust',
    seo: {
      title: 'Our Work - Roofing Project Gallery',
      description: 'View our portfolio of completed roofing projects including repairs and full replacements.',
    },
    sections: [
      {
        type: 'hero',
        data: {
          title: 'Our Recent Projects',
          subtitle: 'Quality craftsmanship you can see',
        }
      },
      {
        type: 'job_showcase',
        data: {
          display_mode: 'gallery',
          show_before_after: true,
          count: 12,
        }
      }
    ],
  },
  {
    id: 'testimonials',
    name: 'Customer Reviews',
    description: 'Social proof from happy customers',
    icon: <TestimonialsIcon />,
    slug: 'testimonials',
    category: 'trust',
    seo: {
      title: 'Customer Reviews - What Our Clients Say',
      description: 'Read reviews from satisfied customers about our roofing services.',
    },
    sections: [
      {
        type: 'hero',
        data: {
          title: 'What Our Customers Say',
          subtitle: 'Real reviews from real customers',
        }
      },
      {
        type: 'testimonials',
        data: {
          source: 'crm',
          count: 10,
          show_all: true,
        }
      },
      {
        type: 'social_proof',
        data: {
          layout: 'centered',
          show_stats: true,
          show_badges: true,
        }
      }
    ],
  },
  {
    id: 'faq',
    name: 'FAQ Page',
    description: 'Answer common questions to save time and build trust',
    icon: <FAQIcon />,
    slug: 'faq',
    category: 'seo',
    seo: {
      title: 'Frequently Asked Questions - Roofing FAQs',
      description: 'Get answers to common roofing questions about repairs, replacements, and maintenance.',
    },
    sections: [
      {
        type: 'hero',
        data: {
          title: 'Frequently Asked Questions',
          subtitle: 'Everything you need to know about our roofing services',
        }
      },
      {
        type: 'faq',
        data: {
          questions: [
            {
              question: 'How do I know if I need a new roof?',
              answer: 'Signs include missing shingles, leaks, age over 20 years, and visible damage.',
            },
            {
              question: 'How long does a roof replacement take?',
              answer: 'Most residential roofs can be replaced in 1-3 days, weather permitting.',
            },
            {
              question: 'Do you offer financing?',
              answer: 'Yes, we offer flexible financing options to make your roof affordable.',
            },
            {
              question: 'What warranty do you provide?',
              answer: 'We offer comprehensive warranties on both materials and workmanship.',
            },
          ]
        }
      }
    ],
  },
  {
    id: 'emergency',
    name: 'Emergency Services',
    description: 'Capture urgent repair searches',
    icon: <ServiceIcon />,
    slug: 'emergency-roofing',
    category: 'conversion',
    seo: {
      title: '24/7 Emergency Roofing Services',
      description: 'Fast emergency roof repair available 24/7. Call now for immediate assistance.',
    },
    sections: [
      {
        type: 'hero',
        data: {
          title: '24/7 Emergency Roofing Services',
          subtitle: 'Fast response when you need it most',
          cta_text: 'Call Now for Emergency Service',
          background_color: 'danger',
        }
      },
      {
        type: 'features',
        data: {
          title: 'Emergency Services We Provide',
          features: [
            { title: 'Storm Damage', description: 'Immediate response to storm damage' },
            { title: 'Leak Repair', description: 'Stop leaks before they cause more damage' },
            { title: 'Tarp Installation', description: 'Temporary protection until repairs can be made' },
            { title: 'Tree Damage', description: 'Remove trees and repair damage quickly' },
          ]
        }
      }
    ],
  },
  {
    id: 'seasonal-offer',
    name: 'Seasonal Promotion',
    description: 'Limited-time offer to drive conversions',
    icon: <OfferIcon />,
    slug: 'spring-special',
    category: 'conversion',
    seo: {
      title: 'Spring Roofing Special - Limited Time Offer',
      description: 'Save 20% on roof replacements this spring. Limited time offer - call today!',
    },
    sections: [
      {
        type: 'hero',
        data: {
          title: 'Spring Roofing Special - Save 20%',
          subtitle: 'Limited time offer ends soon!',
          cta_text: 'Claim Your Discount',
          background_color: 'warning',
        }
      },
      {
        type: 'cta_band',
        data: {
          title: 'Offer Expires in 30 Days',
          subtitle: "Don't miss out on these savings",
          button_text: 'Get Your Free Quote Now',
        }
      }
    ],
  },
];

export const PageTemplates: React.FC<PageTemplatesProps> = ({
  siteId,
  existingPages,
  onPageCreated,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<PageTemplate | null>(null);
  const [customizing, setCustomizing] = useState(false);
  const [customSlug, setCustomSlug] = useState('');
  const [customTitle, setCustomTitle] = useState('');

  const createPageMutation = useMutation({
    mutationFn: async (template: PageTemplate) => {
      const slug = customSlug || template.slug;
      const title = customTitle || template.seo.title;
      
      return api.post('/api/cms/pages/', {
        site: siteId,
        slug: slug,
        template_key: 'generic',
        status: 'draft',
        sections_draft: template.sections,
        seo_draft: {
          title: title,
          description: template.seo.description,
        },
        ai_generation_settings: {
          tone: 'professional',
          auto_generate: true,
        },
      });
    },
    onSuccess: () => {
      onPageCreated();
      setSelectedTemplate(null);
      setCustomizing(false);
      setCustomSlug('');
      setCustomTitle('');
    },
  });

  const handleQuickCreate = (template: PageTemplate) => {
    createPageMutation.mutate(template);
  };

  const handleCustomCreate = (template: PageTemplate) => {
    setSelectedTemplate(template);
    setCustomizing(true);
    setCustomSlug(template.slug);
    setCustomTitle(template.seo.title);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'essential': return 'primary';
      case 'trust': return 'success';
      case 'seo': return 'info';
      case 'conversion': return 'warning';
      default: return 'neutral';
    }
  };

  const availableTemplates = PAGE_TEMPLATES.filter(
    template => !existingPages.includes(template.slug)
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography level="h3">Quick Page Templates</Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          Professional pages with AI-generated content - ready in seconds!
        </Typography>
      </Box>

      {availableTemplates.length === 0 ? (
        <Alert color="success">
          Great job! You've created all recommended pages.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {availableTemplates.map((template) => (
            <Grid xs={12} sm={6} md={4} key={template.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 'sm',
                        bgcolor: `${getCategoryColor(template.category)}.100`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: `${getCategoryColor(template.category)}.600`,
                        mr: 2,
                      }}
                    >
                      {template.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="title-md">{template.name}</Typography>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={getCategoryColor(template.category) as any}
                      >
                        {template.category}
                      </Chip>
                    </Box>
                  </Box>

                  <Typography level="body-sm" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="sm"
                      variant="solid"
                      onClick={() => handleQuickCreate(template)}
                      loading={createPageMutation.isPending}
                      sx={{ flex: 1 }}
                    >
                      Quick Create
                    </Button>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleCustomCreate(template)}
                    >
                      Customize
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Customization Modal */}
      <Modal open={customizing} onClose={() => setCustomizing(false)}>
        <ModalDialog>
          <DialogTitle>Customize Page</DialogTitle>
          <DialogContent>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Page URL</FormLabel>
              <Input
                value={customSlug}
                onChange={(e) => setCustomSlug(e.target.value)}
                startDecorator="/"
                placeholder={selectedTemplate?.slug}
              />
            </FormControl>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Page Title</FormLabel>
              <Input
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder={selectedTemplate?.seo.title}
              />
            </FormControl>

            <Alert variant="soft" color="primary">
              The page will be created with AI-generated content based on your business information.
              You can edit everything after creation.
            </Alert>

            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button
                variant="solid"
                onClick={() => selectedTemplate && createPageMutation.mutate(selectedTemplate)}
                loading={createPageMutation.isPending}
                sx={{ flex: 1 }}
              >
                Create Page
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCustomizing(false)}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
